import { Camera, ImageIcon, ImageOff, Minus, Plus, X } from "lucide-react";
import {
  DAMAGED_REASON_SUGGESTIONS,
  MAX_RETURN_PHOTOS_PER_LINE,
  MISSING_REASON_SUGGESTIONS,
  RETURN_PHOTO_ACCEPT,
  type ReturnParcelLine,
} from "@estelle/domain";
import { useEffect, useId, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { cn } from "@/lib/utils";
import type { LineDraft } from "@/modules/orders/returns/drafts";
import { addPhotos } from "@/modules/orders/returns/photos";

/**
 * One line of a returned parcel, to be checked: how many are good, how many are
 * damaged and how many are missing, which together must be all of what shipped.
 *
 * Built for a phone in a warehouse. Every control is finger-sized on a touch
 * screen (`pointer-coarse`), a line of one is a single tap (Good, Damaged or
 * Missing), a line of several can be split with the steppers, and the camera is
 * one tap away. Nothing starts as "good": a line is unchecked until someone has
 * said what it is, so a confirm is never the product of not looking.
 */

const TOUCH =
  "max-sm:h-11 pointer-coarse:h-11 max-sm:min-w-11 pointer-coarse:min-w-11";

function Stepper({
  label,
  value,
  max,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  max: number;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="w-20 text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={TOUCH}
          aria-label={`One fewer ${label.toLowerCase()}`}
          disabled={disabled || value <= 0}
          onClick={() => {
            onChange(value - 1);
          }}
        >
          <Minus />
        </Button>
        <span
          className="w-8 text-center text-base font-semibold tabular-nums"
          aria-live="polite"
          aria-label={`${label}: ${String(value)}`}
        >
          {value}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={TOUCH}
          aria-label={`One more ${label.toLowerCase()}`}
          disabled={disabled || value >= max}
          onClick={() => {
            onChange(value + 1);
          }}
        >
          <Plus />
        </Button>
      </div>
    </div>
  );
}

function Thumb({ file, onRemove }: { file: File; onRemove: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    // Made and revoked with the effect, so a thumbnail never leaks its file.
    // Handed to state from a microtask, not the effect body, which would
    // render twice for nothing.
    const made = URL.createObjectURL(file);
    let live = true;
    queueMicrotask(() => {
      if (live) setUrl(made);
    });
    return () => {
      live = false;
      URL.revokeObjectURL(made);
    };
  }, [file]);
  return (
    <li className="relative">
      {url ? (
        <img
          src={url}
          alt={`Photo ${file.name}`}
          className="size-16 rounded-md border object-cover"
        />
      ) : (
        <div className="bg-muted size-16 rounded-md border" />
      )}
      <button
        type="button"
        aria-label={`Remove photo ${file.name}`}
        className="bg-background absolute -top-2 -right-2 flex size-7 items-center justify-center rounded-full border shadow-xs after:absolute after:-inset-2.5 after:content-['']"
        onClick={onRemove}
      >
        <X className="size-3.5" />
      </button>
    </li>
  );
}

function Reason({
  label,
  value,
  suggestions,
  required,
  disabled,
  error,
  onChange,
}: {
  label: string;
  value: string;
  suggestions: readonly string[];
  required: boolean;
  disabled: boolean;
  error?: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </Label>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((suggestion) => (
          <Button
            key={suggestion}
            type="button"
            size="sm"
            variant="outline"
            className={cn(
              "max-sm:h-11 pointer-coarse:h-11",
              value === suggestion && SELECTED_CHOICE,
            )}
            aria-pressed={value === suggestion}
            disabled={disabled}
            onClick={() => {
              onChange(suggestion);
            }}
          >
            {suggestion}
          </Button>
        ))}
      </div>
      <Input
        id={id}
        value={value}
        disabled={disabled}
        maxLength={500}
        placeholder="Or write what you see"
        aria-invalid={error !== undefined}
        className="max-sm:h-11 pointer-coarse:h-11"
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      {error ? <p className="text-destructive text-xs">{error}</p> : null}
    </div>
  );
}

function PhotoPicker({
  files,
  required,
  disabled,
  error,
  onChange,
}: {
  files: File[];
  required: boolean;
  disabled: boolean;
  error?: string;
  onChange: (files: File[]) => void;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const full = files.length >= MAX_RETURN_PHOTOS_PER_LINE;

  // Why a file was not added, until the next pick. Shown here and not as a toast:
  // it belongs to this line, and a toast is gone before a gloved hand reads it.
  const [notices, setNotices] = useState<string[]>([]);

  const add = (list: FileList | null) => {
    if (!list) return;
    const picked = addPhotos(files, Array.from(list));
    setNotices(picked.notices);
    if (picked.files.length !== files.length) onChange(picked.files);
  };

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">
        Photo
        {required ? (
          <span className="text-destructive"> * </span>
        ) : (
          <span className="text-muted-foreground font-normal"> (optional)</span>
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="max-sm:h-11 pointer-coarse:h-11"
          disabled={disabled || full}
          onClick={() => camera.current?.click()}
        >
          <Camera />
          Take photo
        </Button>
        <Button
          type="button"
          variant="outline"
          className="max-sm:h-11 pointer-coarse:h-11"
          disabled={disabled || full}
          onClick={() => gallery.current?.click()}
        >
          <ImageIcon />
          Choose photo
        </Button>
      </div>
      {/* The camera, where the device has one; elsewhere a file picker. */}
      <input
        ref={camera}
        type="file"
        accept={RETURN_PHOTO_ACCEPT}
        capture="environment"
        multiple
        hidden
        data-testid="photo-camera"
        onChange={(event) => {
          add(event.target.files);
          event.target.value = "";
        }}
      />
      <input
        ref={gallery}
        type="file"
        accept={RETURN_PHOTO_ACCEPT}
        multiple
        hidden
        data-testid="photo-gallery"
        onChange={(event) => {
          add(event.target.files);
          event.target.value = "";
        }}
      />
      {files.length > 0 ? (
        <ul className="flex flex-wrap gap-3 pt-1">
          {files.map((file, index) => (
            <Thumb
              key={`${file.name}-${String(index)}`}
              file={file}
              onRemove={() => {
                onChange(files.filter((_, i) => i !== index));
              }}
            />
          ))}
        </ul>
      ) : null}
      {notices.length > 0 ? (
        <ul role="alert" className="text-destructive space-y-0.5 text-xs">
          {notices.map((notice) => (
            <li key={notice}>{notice}</li>
          ))}
        </ul>
      ) : null}
      {error ? <p className="text-destructive text-xs">{error}</p> : null}
    </div>
  );
}

export function LineChecker({
  line,
  draft,
  warehouseName,
  stockAt,
  disabled,
  errors,
  onChange,
  onViewImage,
}: {
  line: ReturnParcelLine;
  draft: LineDraft;
  /** The warehouse the good items will go back to, for the stock figure. */
  warehouseName: string | null;
  stockAt: number | null;
  disabled: boolean;
  /** What the server or the checks said, by `lines.<id>…` suffix. */
  errors: {
    line?: string;
    damagedReason?: string;
    missingReason?: string;
    photos?: string;
  };
  onChange: (draft: LineDraft) => void;
  /** Opens the product picture full size; absent when there is nowhere to show it. */
  onViewImage?: () => void;
}) {
  const checked = draft.good + draft.damaged + draft.missing;
  const left = line.quantity - checked;
  const single = line.quantity === 1;

  const set = (patch: Partial<LineDraft>) => {
    onChange({ ...draft, ...patch });
  };
  /** A stepper moves one count, never past what shipped. */
  const step = (key: "good" | "damaged" | "missing", value: number) => {
    const others = checked - draft[key];
    set({ [key]: Math.max(0, Math.min(value, line.quantity - others)) });
  };
  const all = (key: "good" | "damaged" | "missing") => {
    set({
      good: key === "good" ? line.quantity : 0,
      damaged: key === "damaged" ? line.quantity : 0,
      missing: key === "missing" ? line.quantity : 0,
    });
  };

  const chosen =
    draft.good === line.quantity
      ? "good"
      : draft.damaged === line.quantity
        ? "damaged"
        : draft.missing === line.quantity
          ? "missing"
          : null;

  return (
    <li
      className={cn(
        "space-y-3 rounded-lg border p-3",
        left === 0 ? "border-emerald-500/40" : "",
      )}
      data-line={line.orderLineItemId}
    >
      <div className="flex items-start gap-3">
        {line.imageUrl ? (
          <button
            type="button"
            aria-label={`View ${line.title} full size`}
            className="size-14 shrink-0 cursor-zoom-in overflow-hidden rounded-md border max-sm:size-20"
            onClick={onViewImage}
          >
            <img
              src={line.imageUrl}
              alt=""
              loading="lazy"
              className="size-full object-cover"
            />
          </button>
        ) : (
          <div
            aria-hidden
            className="bg-muted text-muted-foreground flex size-14 shrink-0 items-center justify-center rounded-md border"
          >
            <ImageOff className="size-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{line.title}</p>
          {line.variantTitle ? (
            <p className="text-muted-foreground text-xs">{line.variantTitle}</p>
          ) : null}
          {line.sku ? (
            <p className="text-muted-foreground text-xs">SKU {line.sku}</p>
          ) : null}
          <p className="text-muted-foreground text-xs">
            {line.restockable
              ? stockAt !== null && warehouseName
                ? `In stock now at ${warehouseName}: ${String(stockAt)}`
                : "Not stocked at this warehouse"
              : "No stock item: good units are recorded, nothing to put back"}
          </p>
        </div>
        <p className="shrink-0 text-right text-sm">
          <span className="text-muted-foreground text-xs">Shipped</span>
          <br />
          <span className="text-lg font-semibold tabular-nums">
            {line.quantity}
          </span>
        </p>
      </div>

      <div
        role="group"
        aria-label={`Check ${line.title}`}
        className="grid grid-cols-3 gap-2"
      >
        {(
          [
            ["good", "Good"],
            ["damaged", "Damaged"],
            ["missing", "Missing"],
          ] as const
        ).map(([key, label]) => (
          <Button
            key={key}
            type="button"
            variant="outline"
            className={cn(
              "max-sm:h-12 pointer-coarse:h-12",
              chosen === key && SELECTED_CHOICE,
              chosen === key &&
                key === "damaged" &&
                "text-destructive hover:text-destructive outline-destructive",
            )}
            aria-pressed={chosen === key}
            disabled={disabled}
            onClick={() => {
              all(key);
            }}
          >
            {single ? label : `All ${label.toLowerCase()}`}
          </Button>
        ))}
      </div>

      {single ? null : (
        <div className="space-y-2 border-t pt-3">
          <p className="text-muted-foreground text-xs">
            Split them: counts must add up to {line.quantity}.
          </p>
          <Stepper
            label="Good"
            value={draft.good}
            max={line.quantity}
            disabled={disabled}
            onChange={(value) => {
              step("good", value);
            }}
          />
          <Stepper
            label="Damaged"
            value={draft.damaged}
            max={line.quantity}
            disabled={disabled}
            onChange={(value) => {
              step("damaged", value);
            }}
          />
          <Stepper
            label="Missing"
            value={draft.missing}
            max={line.quantity}
            disabled={disabled}
            onChange={(value) => {
              step("missing", value);
            }}
          />
        </div>
      )}

      {left > 0 ? (
        <p className="text-sm text-amber-700 dark:text-amber-300" role="status">
          {single
            ? "Not checked yet."
            : `${String(left)} of ${String(line.quantity)} not checked yet.`}
        </p>
      ) : null}
      {errors.line && left === 0 ? (
        <p className="text-destructive text-sm">{errors.line}</p>
      ) : null}

      {draft.damaged > 0 ? (
        <div className="bg-muted/40 space-y-3 rounded-md border p-3">
          <p className="text-sm font-semibold">
            {draft.damaged} damaged: what is wrong, and a photo
          </p>
          <Reason
            label="What is wrong with it"
            value={draft.damagedReason}
            suggestions={DAMAGED_REASON_SUGGESTIONS}
            required
            disabled={disabled}
            error={errors.damagedReason}
            onChange={(damagedReason) => {
              set({ damagedReason });
            }}
          />
          <PhotoPicker
            files={draft.photos}
            required
            disabled={disabled}
            error={errors.photos}
            onChange={(photos) => {
              set({ photos });
            }}
          />
        </div>
      ) : null}

      {draft.missing > 0 ? (
        <div className="bg-muted/40 space-y-3 rounded-md border p-3">
          <p className="text-sm font-semibold">
            {draft.missing} missing: what happened
          </p>
          <Reason
            label="What happened to it"
            value={draft.missingReason}
            suggestions={MISSING_REASON_SUGGESTIONS}
            required
            disabled={disabled}
            error={errors.missingReason}
            onChange={(missingReason) => {
              set({ missingReason });
            }}
          />
          {draft.damaged === 0 ? (
            <PhotoPicker
              files={draft.photos}
              required={false}
              disabled={disabled}
              onChange={(photos) => {
                set({ photos });
              }}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
